import { Address, AddressApiResponse } from '@core/model/form/address.model';
import { format } from 'date-fns';
import { UserPreferences } from '@core/model/user/user-preferences.model';

export interface OrganizationApiResponse {
  id: number;
  name: string;
  email: string;
  organizationSet: string;
  organizationNumber: string;
  isImportedNapo: string;
  schedule: string;
  deliverySchedule: string;
  fileName: string;
  bic: string;
  iban: string;
  accountHolder: string;
  mailOrder: string;
  fiscalCode: string;
  ustId: string;
  taxNumber: string;
  ik: string;
  isPharmacy: string;
  industryKey: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  addresses: Array<AddressApiResponse>;
  preferences: UserPreferences;
}

export interface OrganizationModel {
  id: number;
  name: string;
  email: string;
  organizationSet: string;
  organizationNumber: string;
  isImportedNapo: string;
  schedule: string;
  deliverySchedule: string;
  fileName: string;
  bic: string;
  iban: string;
  accountHolder: string;
  mailOrder: string;
  fiscalCode: string;
  ustId: string;
  taxNumber: string;
  ik: string;
  isPharmacy: string;
  industryKey: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  deletedAt: Date | null;
  addresses: Array<AddressApiResponse>;
  preferences: UserPreferences;
}

export class Organization implements OrganizationModel {
  id = -1;
  name = '';
  email = '';
  organizationSet = '';
  organizationNumber = '';
  isImportedNapo = '';
  schedule = '';
  deliverySchedule = '';
  fileName = '';
  bic = '';
  iban = '';
  accountHolder = '';
  mailOrder = '';
  fiscalCode = '';
  ustId = '';
  taxNumber = '';
  ik = '';
  isPharmacy = '';
  industryKey = '';
  createdAt = new Date();
  updatedAt = new Date();
  deletedAt = new Date();
  addresses = new Array<Address>();
  preferences = new UserPreferences();

  constructor(dto?: OrganizationModel) {
    if (!dto) {
      return;
    }
    this.id = dto.id;
    this.name = dto.name;
    this.email = dto.email;
    this.organizationSet = dto.organizationSet;
    this.organizationNumber = dto.organizationNumber;
    this.isImportedNapo = dto.isImportedNapo;
    this.schedule = dto.schedule;
    this.deliverySchedule = dto.deliverySchedule;
    this.fileName = dto.fileName;
    this.bic = dto.bic;
    this.iban = dto.iban;
    this.accountHolder = dto.accountHolder;
    this.mailOrder = dto.mailOrder;
    this.fiscalCode = dto.fiscalCode;
    this.ustId = dto.ustId;
    this.taxNumber = dto.taxNumber;
    this.ik = dto.ik;
    this.isPharmacy = dto.isPharmacy;
    this.industryKey = dto.industryKey;
    this.createdAt = dto.createdAt ? new Date(dto.createdAt) : this.createdAt;
    this.updatedAt = dto.updatedAt ? new Date(dto.updatedAt) : this.updatedAt;
    this.deletedAt = dto.deletedAt ? new Date(dto.deletedAt) : this.deletedAt;
    this.addresses = dto.addresses?.length ? dto.addresses : [];
    this.preferences = dto.preferences;
  }

  static parseDTO(dto: OrganizationApiResponse): Organization {
      return new Organization({
        id: dto.id,
        name: dto.name,
        email: dto.email,
        organizationSet: dto.organizationSet,
        organizationNumber: dto.organizationNumber,
        schedule: dto.schedule,
        deliverySchedule : dto.deliverySchedule,
        isImportedNapo: dto.isImportedNapo,
        fileName: dto.fileName,
        bic: dto.bic,
        iban: dto.iban,
        accountHolder: dto.accountHolder,
        mailOrder: dto.mailOrder,
        fiscalCode: dto.fiscalCode,
        ustId: dto.ustId,
        taxNumber: dto.taxNumber,
        ik: dto.ik,
        isPharmacy: dto.isPharmacy,
        industryKey: dto.industryKey,
        createdAt: dto.createdAt ? new Date(dto.createdAt) : null,
        updatedAt: dto.updatedAt ? new Date(dto.updatedAt) : null,
        deletedAt: dto.deletedAt ? new Date(dto.deletedAt) : null,
        addresses: dto.addresses?.length ? Address.parseDto(dto.addresses) : [],
        preferences: dto.preferences
    });
  }

  static jsonSerialize(org: Organization): OrganizationApiResponse {
      return {
        id : org.id,
        name: org.name,
        email: org.email,
        organizationSet : org.organizationSet,
        organizationNumber: org.organizationNumber,
        schedule : org.schedule,
        deliverySchedule: org.deliverySchedule,
        fileName: org.fileName,
        bic: org.bic,
        iban: org.iban,
        accountHolder: org.accountHolder,
        isImportedNapo: org.isImportedNapo,
        mailOrder: org.mailOrder,
        fiscalCode: org.fiscalCode,
        ustId: org.ustId,
        taxNumber: org.taxNumber,
        ik: org.ik,
        isPharmacy: org.isPharmacy,
        industryKey: org.industryKey,
        createdAt: org.createdAt ? format(org.createdAt, 'yyyy-MM-dd'): '',
        updatedAt: org.updatedAt ? format(org.updatedAt, 'yyyy-MM-dd'): '',
        deletedAt: org.deletedAt ? format(org.deletedAt, 'yyyy-MM-dd'): '',
        addresses: org.addresses ? Address.jsonSerialize(org.addresses) : [],
        preferences: org.preferences
      };
  }
}
